"use client";

import {
  ImageAPI,
  ImageSearchRequest,
  ImageResponse,
  ImagesResponseData,
} from "@/restapi/image";
import useSWRInfinite from "swr/infinite";

/**
 * 画像データを取得するための非同期フェッチ関数
 */
const fetcher = async (
  _key: string,
  page: number,
  request: ImageSearchRequest
): Promise<ImageResponse> => {
  return ImageAPI.search({ ...request, page });
};

const buildSearchKey = (request: ImageSearchRequest, page: number): string => {
  return `image-search:${JSON.stringify(request)}:page=${page}`;
};

/**
 * 画像一覧を検索するカスタムフック
 */
export const useImageSearch = (
  request: ImageSearchRequest,
  initialSize: number
): ImagesResponseData => {
  const refreshInterval = request.refreshInterval ?? 6000;

  const getKey = (
    index: number,
    previousPageData: ImageResponse | null
  ): string | null => {
    if (previousPageData && !previousPageData.data.length) {
      return null;
    }

    return buildSearchKey(request, index + 1);
  };

  const fetcherWrapper = async (key: string): Promise<ImageResponse> => {
    const match = key.match(/page=(\d+)/);
    const page = match ? parseInt(match[1], 10) : 1;
    return fetcher(key, page, request);
  };

  const { data, error, size, setSize, isValidating } =
    useSWRInfinite<ImageResponse>(getKey, fetcherWrapper, {
      initialSize,
      refreshInterval,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0,
    });

  // 初回ロード判定（SWRInfinite は isLoading を返さないため
  // !data && !error で初回取得中を判定）
  const isLoading = !data && !error;
  const lastPage = data ? data[data.length - 1]?.lastPage ?? 1 : 1;
  const hasMore = data ? data[data.length - 1]?.currentPage < lastPage : false;
  const items = data ? data.flatMap((pageData) => pageData.data) : [];
  const lockedCound = data ? data[data.length - 1]?.lockedCount ?? 0 : 0;

  const loadMore = () => {
    if (!isValidating && hasMore) {
      setSize((prev) => prev + 1);
    }
  };

  return {
    items,
    isLoading,
    isValidating,
    isError: !!error,
    loadMore,
    hasMore,
    lockedCound,
  };
};